import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, Table, Popconfirm, Typography, Space, Divider, message, Tooltip } from "antd";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ReactComponent as Copy } from "../../assets/icons/Copy.svg";
import { ReactComponent as Trash } from "../../assets/icons/Trash.svg";
import { ReactComponent as Plus } from "../../assets/icons/Plus.svg";
import { DATE_STRING } from "../../model/schedule";
import { ulid } from "ulid";
import LoaderButton from "../../components/LoaderButton";
import { LinkContainer } from "react-router-bootstrap";
import dayjs from "../../dayjs";
import useAllCustomers from "../Customers/useAllCustomers";
import DateCarousel from "./DateCarousel";
import { FilterTags } from "../../containers/FilterTag";
import { filterItems, quoteFilterItems } from "./BookingFilters";
import { useBooking } from "./BookingContext";
import { Charter } from "../../model/charter";
import { charterModelData, routeModelData, shiftBatModelData } from "../../services/ModelService";
import useAllCharters from "../Charters/useAllCharters";
import { Warning } from "../../components/Icons";

const { Title } = Typography;

export function BookingList({setDate}) {
  const history = useHistory();
  const { allCustomers } = useAllCustomers();
  const [isDeleting, setIsDeleting] = useState({});
  const [isCloning, setIsCloning] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const listRef = useRef(null);
  const contentRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const { filteredBookings, bookings: allBookings, filter, filterBy } = useBooking();
  const { allChartersArray } = useAllCharters();
  const scrollTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);
  const prevFilterRef = useRef({ filter, filterBy });

  // Memoize columns to avoid recreation on every render
  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Booking Reference",
      dataIndex: "bookingReference",
      key: "bookingReference",
      className: 'text-center',
      width: "10%",
      render: (bookingReference) => bookingReference || "-",
    },
    {
      title: "Quote",
      key: "quote",
      className: 'text-center',
      width: "15%",
      render: (_, charter) => {
        if (charter.quoteLineItems && charter.quoteLineItems.length > 0) {
          const subtotal = charter.quoteLineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
          const totalWithGST = subtotal * 1.1;
          return totalWithGST ? `$${Math.round(totalWithGST).toLocaleString()}` : "-";
        }
        return "-";
      },
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      className: 'text-center',
      width: "15%",
      render: (customer) => customer?.name || "-",
    },
    {
      title: "Passengers",
      dataIndex: "passengerCount",
      key: "passengers",
      className: 'text-center',
      width: "15%",
      render: (passengerCount) => passengerCount ? `${passengerCount}` : "-",
    },
    {
      title: "Booking Status",
      dataIndex: "status",
      key: "status",
      className: 'text-center',
      width: '9%',
      render: (_, charter) => (
        <div className="d-flex simple-tags w-100 justify-content-center">
          <FilterTags filterItems={filterItems} model={charter} />
        </div>
      ),
    },
    {
      title: "Quote Status",
      dataIndex: "quoteStatus",
      key: "quoteStatus",
      className: 'text-center',
      width: '9%',
      render: (_, charter) => (
        <div className="d-flex simple-tags w-100 justify-content-center">
          <FilterTags filterItems={quoteFilterItems} model={charter} />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      className: 'text-center',
      width: "6%",
      render: (_, charter) => {
        const validationsMessages = charter.getValidationsMessages();
        return (<div className="d-flex justify-content-around align-items-center">
          {validationsMessages && validationsMessages.length > 0 && (
            <Tooltip title={<div>
              {validationsMessages.map(v => (
                <p>{v.message}</p>
              ))}
            </div>}>
              <Warning style={{ height: 14 }} />
            </Tooltip>
          )}
          <LoaderButton
            className="btn-icon-control"
            size="sm"
            isLoading={isCloning}
            icon={<Copy />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleClone(charter);
            }}
          />
          <Popconfirm
            title="Delete this booking?"
            onConfirm={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleDelete(charter);
            }}
            onCancel={(e) => e.stopPropagation()}
            okText="Yes"
            cancelText="No"
          >
            <LoaderButton className={`btn-icon-control btn-delete`} size="sm" isLoading={isDeleting[charter.charterId]} icon={<Trash />} onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </div>)
      },
    },
  ], [isCloning, isDeleting]);

  // Memoize groupedData (group bookings by date)
  const groupedData = useMemo(() => {
    if (!filteredBookings) return {};
    const grouped = filteredBookings.reduce((acc, charter) => {
      const dates = charter.dates?.length ? charter.dates : [charter.startDate];
      dates.forEach(date => {
        const dateKey = date?.format ? date.format("YYYY-MM-DD") : null;
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(charter);
      });
      if (dates.length === 0) {
        if (!acc['No Date']) {
          acc['No Date'] = [];
        }
        acc['No Date'].push(charter);
      }
      return acc;
    }, {});
    // Sort bookings within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const dateA = dayjs(a.startDate, DATE_STRING);
        const dateB = dayjs(b.startDate, DATE_STRING);
        return dateA.diff(dateB);
      });
    });
    return grouped;
  }, [filteredBookings]);

  // Memoize generateDateRange
  const generateDateRange = useMemo(() => {
    const allDates = Object.keys(groupedData)
      .filter(date => date !== 'No Date')
      .map(date => dayjs(date))
      .filter(date => date.isValid());

    if (filter || filterBy.length > 0) {
      return allDates
        .filter(date => {
          const dateKey = date.format('YYYY-MM-DD');
          return groupedData[dateKey] && groupedData[dateKey].length > 0;
        })
        .map(date => date.format('YYYY-MM-DD'))
        .sort((a, b) => dayjs(a).diff(dayjs(b)));
    }

    // For normal view, get the min and max dates from the actual data
    const minDate = allDates.length > 0 
      ? dayjs.min(allDates).subtract(365, 'day') // Add a year before earliest booking
      : dayjs().subtract(365, 'day');
    
    const maxDate = allDates.length > 0
      ? dayjs.max(allDates).add(365, 'day') // Add a year after latest booking
      : dayjs().add(365, 'day');

    // Generate all dates between min and max
    const dateRange = [];
    let currentDate = minDate;
    
    while (currentDate.isBefore(maxDate) || currentDate.isSame(maxDate, 'day')) {
      dateRange.push(currentDate.format('YYYY-MM-DD'));
      currentDate = currentDate.add(1, 'day');
    }

    // Sort all dates chronologically
    return dateRange.sort((a, b) => dayjs(a).diff(dayjs(b)));
  }, [groupedData, filter, filterBy]);

  // Limit rendered dates to 9 before and 9 after selected date (19 total)
  const visibleDates = useMemo(() => {
    // If we're filtering, show all filtered dates
    if (filter || filterBy.length > 0) {
      return generateDateRange;
    }

    // For normal view, find the selected date in the range
    const selectedKey = selectedDate.format('YYYY-MM-DD');
    const idx = generateDateRange.findIndex(d => d === selectedKey);
    
    // If selected date not found, default to today
    if (idx === -1) {
      const today = dayjs().format('YYYY-MM-DD');
      const todayIdx = generateDateRange.findIndex(d => d === today);
      if (todayIdx === -1) return generateDateRange.slice(0, 19); // fallback
      return generateDateRange.slice(Math.max(0, todayIdx - 9), todayIdx + 10);
    }

    // Show 9 dates before and 9 after the selected date
    return generateDateRange.slice(Math.max(0, idx - 9), idx + 10);
  }, [generateDateRange, selectedDate, filter, filterBy]);

  const handleClone = async (charter) => {
    setIsCloning(true);
    try {
      console.log('Copy charter', charter.charterId);
      const clonedCharter = charter.clone();
      clonedCharter.charterId = ulid();
      clonedCharter.bookingReference = charter.generateBookingReference(allChartersArray)
      clonedCharter.name += '-COPY';
      clonedCharter.status = 'new';
      clonedCharter.createdAt = new Date().toISOString();
      clonedCharter.updatedAt = new Date().toISOString();
      // copy itinerary
      clonedCharter.itinerary = clonedCharter.itinerary.map(route => {
        route.routeId = ulid();
        return route;
      })
      await routeModelData.save(clonedCharter.itinerary);
      // copy duties
      clonedCharter.duties = clonedCharter.duties.map(duty => {
        duty.shiftBatId = ulid();
        return duty;
      })
      await shiftBatModelData.save(clonedCharter.duties);
      await charterModelData.save(clonedCharter)
      console.log('Charter saved.');
    } finally {
      setIsCloning(false);
    }
  };

  const handleDelete = async (charter) => {
    setIsDeleting((prev) => ({ ...prev, [charter.charterId]: true }));
    try {
      charterModelData.delete([charter.charterId, charter.itineraryId], true).then(() => {
        console.log('Charter deleted.');
        shiftBatModelData.delete(charter.duties.map(duty => duty.shiftBatId), true).then(() => {
          console.log(`Deleted ${charter.duties.length} duties. for charter $${charter.charterId}`);
        })
        setIsDeleting({ ...isDeleting, [charter.charterId]: false });
      }).then(() => {
        routeModelData.delete(charter.itinerary.map(route => route.routeId), true).then(() => {
          console.log(`Deleted ${charter.itinerary.length} routes. for charter $${charter.charterId}`);
        })
      });
    } finally {
      setIsDeleting((prev) => ({ ...prev, [charter.charterId]: false }));
    }
  };

  // Add effect to scroll to today on initial load
  useEffect(() => {
    if (initialLoadRef.current && Object.keys(groupedData).length > 0) {
      initialLoadRef.current = false;
      const today = dayjs();
      setSelectedDate(today);
      setDate(today);
      
      // After a short delay to ensure the DOM has updated, scroll to today's date
      setTimeout(() => {
        const dateKey = today.format("YYYY-MM-DD");
        const element = document.getElementById(`date-group-${dateKey}`);
        if (element && contentRef.current) {
          // Calculate the scroll position to bring the element into view
          const containerRect = contentRef.current.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;

          // Scroll the content container without animation for immediate positioning
          contentRef.current.scrollTo({
            top: contentRef.current.scrollTop + relativeTop,
            behavior: 'auto'
          });
        }
        
        // After scrolling is complete, enable user scrolling
        setIsUserScrolling(true);
      }, 100);
    }
  }, [groupedData]);

  // Handle scroll to update selected date
  const handleScroll = useCallback(() => {
    if (!isScrolling && isUserScrolling) {
      setIsScrolling(true);

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set a new timeout to process the scroll after scrolling has stopped
      scrollTimeoutRef.current = setTimeout(() => {
        const container = contentRef.current;
        if (!container) {
          setIsScrolling(false);
          return;
        }

        // Get all date groups by their ID pattern
        const dateGroups = Array.from(container.querySelectorAll('[id^="date-group-"]'));
        const containerTop = container.getBoundingClientRect().top;
        
        // Find the date group at the top of the viewport
        let topGroup = null;
        let minDistance = Infinity;

        dateGroups.forEach((group) => {
          const rect = group.getBoundingClientRect();
          // Check if the group is at or near the top of the container
          if (rect.top >= containerTop - 100 && rect.top <= containerTop + 100) {
            const distance = Math.abs(rect.top - containerTop);
            if (distance < minDistance) {
              minDistance = distance;
              topGroup = group;
            }
          }
        });

        // If we found a group at the top, update the selected date
        if (topGroup) {
          const dateKey = topGroup.getAttribute("data-date");
          if (dateKey) {
            const newDate = dayjs(dateKey);
            if (!newDate.isSame(selectedDate, "day")) {
              setSelectedDate(newDate);
              setDate(newDate);
            }
          }
        }

        setIsScrolling(false);
      }, 150); // Debounce time of 150ms
    }
  }, [isScrolling, isUserScrolling, selectedDate, setDate]);

  // Handle date selection and scroll to the selected date's bookings
  const handleDateSelect = (date) => {
    // Temporarily disable scroll detection
    setIsUserScrolling(false);
    
    // Update the selected date
    setSelectedDate(date);
    setDate(date);
    
    const dateKey = date.format("YYYY-MM-DD");

    // Use a small delay to ensure the DOM has updated
    setTimeout(() => {
      const element = document.getElementById(`date-group-${dateKey}`);
      if (element && contentRef.current) {
        // Calculate the scroll position to bring the element into view
        const containerRect = contentRef.current.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;

        // Scroll the content container with smooth animation
        contentRef.current.scrollTo({
          top: contentRef.current.scrollTop + relativeTop,
          behavior: 'smooth'
        });
      }
      
      // Re-enable scroll detection after scrolling is complete
      setTimeout(() => {
        setIsUserScrolling(true);
      }, 500);
    }, 100);
  };

  // Add effect to scroll to closest filtered result when search/filter changes
  useEffect(() => {
    // Check if this is a new filter/search change
    const isNewFilter = prevFilterRef.current.filter !== filter || 
      prevFilterRef.current.filterBy !== filterBy;
    
    // Update the previous filter reference
    prevFilterRef.current = { filter, filterBy };

    // Only run if we have a search term or filters active AND it's a new filter change
    if ((filter || filterBy.length > 0) && isNewFilter) {
      // Temporarily disable scroll detection
      setIsUserScrolling(false);
      
      if (filteredBookings?.length > 0) {
        // Get all result dates
        const resultDates = generateDateRange;
        
        // Find the closest date to today
        const today = dayjs();
        let closestDate = resultDates[0];
        let minDiff = Math.abs(dayjs(resultDates[0]).diff(today, 'days'));
        
        resultDates.forEach(date => {
          const diff = Math.abs(dayjs(date).diff(today, 'days'));
          if (diff < minDiff) {
            minDiff = diff;
            closestDate = date;
          }
        });

        const dateKey = closestDate;
        
        // Use a small delay to ensure the DOM has updated
        setTimeout(() => {
          const element = document.getElementById(`date-group-${dateKey}`);
          if (element && contentRef.current) {
            // Calculate the scroll position to bring the element into view
            const containerRect = contentRef.current.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;

            // Scroll the content container with smooth animation
            contentRef.current.scrollTo({
              top: contentRef.current.scrollTop + relativeTop,
              behavior: 'smooth'
            });

            // Update the selected date to match
            setSelectedDate(dayjs(closestDate));
            setDate(dayjs(closestDate));
          }
          
          // Re-enable scroll detection after scrolling is complete
          setTimeout(() => {
            setIsUserScrolling(true);
          }, 500);
        }, 100);
      } else {
        // No results - anchor to current date
        const today = dayjs();
        const dateKey = today.format("YYYY-MM-DD");
        
        setTimeout(() => {
          const element = document.getElementById(`date-group-${dateKey}`);
          if (element && contentRef.current) {
            // Calculate the scroll position to bring the element into view
            const containerRect = contentRef.current.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;

            // Scroll the content container with smooth animation
            contentRef.current.scrollTo({
              top: contentRef.current.scrollTop + relativeTop,
              behavior: 'smooth'
            });

            // Update the selected date to today
            setSelectedDate(today);
            setDate(today);
          }
          
          // Re-enable scroll detection after scrolling is complete
          setTimeout(() => {
            setIsUserScrolling(true);
          }, 500);
        }, 100);
      }
    }
  }, [filter, filterBy, filteredBookings, generateDateRange]);

  return (
    <div className="booking-list-container" ref={listRef}>
      <div className="booking-list-header">
        <DateCarousel selectedDate={selectedDate} onDateSelect={handleDateSelect} bookings={allBookings || []} />
        {(filter || filterBy.length > 0) && (
          <div style={{ 
            padding: '8px 15px', 
            color: '#939CAE', 
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>Found {filteredBookings?.length || 0} results</span>
            {filteredBookings?.length > 0 && (
              <span>across {generateDateRange.length} {generateDateRange.length === 1 ? 'day' : 'days'}</span>
            )}
          </div>
        )}
      </div>

      <div
        ref={contentRef}
        onScroll={handleScroll}
        style={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          paddingBottom: '500px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          paddingLeft: '15px',
          paddingRight: '15px',
          width: 'calc(100% + 30px)',
          marginLeft: '-15px',
          marginRight: '-15px',
        }}
      >
        {/* Generate a range of dates to display */}
        {visibleDates.map(dateKey => (
          <div
            key={dateKey}
            id={`date-group-${dateKey}`}
            data-date={dateKey}
            className="date-group"
          >
            <Title level={5} style={{ fontWeight: 600, fontSize: 16, color: '#939CAE', padding: '6px 15px' }}>
              {dayjs(dateKey).format("dddd, Do MMMM")}
            </Title>
            <div className="CardTable">
              {groupedData[dateKey] && groupedData[dateKey].length > 0 ? ( <>
                <Table
                  size="middle"
                  key={`booking-${dateKey}`}
                  columns={columns}
                  dataSource={groupedData[dateKey]}
                  pagination={false}
                  virtual={true}
                  onRow={(charter) => ({
                    onClick: (e) => {
                      if (e.ctrlKey || e.metaKey) {
                        window.open(`/bookings/${charter.charterId}`, "_blank");
                      } else {
                        history.push(`/bookings/${charter.charterId}`);
                      }
                    },
                  })}
                  rowKey={(charter) => charter.charterId}
                  rowClassName={() => "booking-row"}
                />
                <div className="d-flex justify-content-center align-items-end" style={{minHeight: '33px'}}>
                  <Button
                    type="primary"
                    className="icon-button btn-sm mb-1"
                    icon={<PlusOutlined />}
                    onClick={() => history.push(`/bookings/_new_?date=${dateKey}`)}>Add Booking</Button>
                </div>
              </>
              ) : (
                <div className="no-bookings-message">
                  <div className="no-bookings-message-add-button">
                    <span>Bookings available</span>
                    <Button
                      type="primary"
                      className="icon-button btn-sm"
                      icon={<PlusOutlined />}
                      onClick={() => history.push(`/bookings/_new_?date=${dateKey}`)}>Add Booking</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {/* Handle "No Date" bookings if they exist */}
        {groupedData['No Date'] && groupedData['No Date'].length > 0 && (
          <div
            id="date-group-No-Date"
            data-date="No-Date"
            className="date-group"
          >
            <Title level={5} className="mb-2" style={{ fontWeight: 500, fontSize: 16, color: '#939CAE' }}>
              No Date
            </Title>
            <div className="CardTable mb-4">
              <Table
                size="middle"
                key="booking-No-Date"
                columns={columns}
                dataSource={groupedData['No Date']}
                pagination={false}
                virtual={true}
                showHeader={false}
                onRow={(charter) => ({
                  onClick: (e) => {
                    if (e.ctrlKey || e.metaKey) {
                      window.open(`/bookings/${charter.charterId}`, "_blank");
                    } else {
                      history.push(`/bookings/${charter.charterId}`);
                    }
                  },
                })}
                rowKey={(charter) => charter.charterId}
                rowClassName={() => "booking-row"}
              />
            </div>
          </div>
        )}

        {/* Add empty space for extra scrolling */}
        <div style={{ height: '300px' }}></div>
      </div>
    </div>
  );
}
